import mixpanel, { Mixpanel as MixpanelIntf, RequestOptions } from 'mixpanel-browser'

// Constants
import {
  CONFIG_MIXPANEL_PROJECT_ID,
  CONFIG_MIXPANEL_PROXY_API_ENDPOINT,
  CONFIG_MIXPANEL_PROXY_PROJECT_ID,
} from '@src/config/env'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getUserIp } from '@src/quince-site-common/src/services/ipService/ipService'
import { createNewSessionIfNeeded } from '@src/quince-site-common/src/trackers/initializeAnalytics'
import { checkApiBlocked } from '@src/quince-site-common/src/utils/checkApiBlocked'
import { canTrack } from '@src/quince-site-common/src/utils/checkDoNotTrack'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { eraseFromLocalStorage } from '@src/quince-site-common/src/utils/eraseFromLocalStorageWithExpiry'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import { captureException, captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'
import { setInLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/setInLocalStorageWithExpiry'
import { handleSgtmApi } from '@src/telemetry/handleSgtmApi'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'

// Utils
import { trackEvent } from '@src/telemetry/track'

import AuthUtil from '../../../quince-site-common/src/utils/authUtil'
import eraseCookie from '../../../quince-site-common/src/utils/eraseCookie'
import generateAlphaNumericId from '../../../quince-site-common/src/utils/generateAlphaNumericId'
import getCookie from '../../../quince-site-common/src/utils/getCookie'
import setCookie from '../../../quince-site-common/src/utils/setCookie'

// Helpers

import { getCommonProperties, getTransformedProperties, isBotAgent } from './mixpanel.helper'
import { createLastBrandUserId, getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'

// Services

export class Mixpanel {
  private isInitialised = false
  private mpQueue: Record<string, any>[] = []

  init() {
    try {
      const isTrackingEnabled = canTrack()
      if (!isTrackingEnabled) {
        trackEvent(TELEMETRY_EVENT_NAMES.DO_NOT_TRACK, {
          context: {
            trackingEnabled: false,
          },
        })
      }

      checkApiBlocked('https://api-js.mixpanel.com/track/?verbose=1&ip=1&_=1721377158412').then(
        ({ blocked, errorType }) => {
          if (blocked) {
            trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
              apiName: 'mixpanelTrack',
              status: 'blocked',
              errorType,
            })
          }
        },
      )

      if (CONFIG_MIXPANEL_PROJECT_ID && CONFIG_MIXPANEL_PROXY_PROJECT_ID && CONFIG_MIXPANEL_PROXY_API_ENDPOINT) {
        getUserIp()

        mixpanel.init(CONFIG_MIXPANEL_PROJECT_ID, {
          persistence: 'localStorage',
          debug: true,
          batch_requests: false,
        })
        mixpanel.init(
          CONFIG_MIXPANEL_PROXY_PROJECT_ID,
          {
            persistence: 'localStorage',
            debug: true,
            loaded: this.initialisationCheck,
            api_host: CONFIG_MIXPANEL_PROXY_API_ENDPOINT,
            batch_requests: false,
          },
          'proxy_instance',
        )
      }
    } catch (error) {
      captureFatalException(`Failed to initialize mixpanel : ${error}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  identify({ email, isTentativeEmail = true }: { email: string; isTentativeEmail?: boolean }) {
    try {
      if (email) {
        if (isTentativeEmail) {
          this.setMixpanelTentativeEmail(email)
        } else {
          this.setMixpanelUserId(email)
          mixpanel.identify(email)
        }
      }
    } catch (error) {
      captureException(`Failed to identify email : ${error}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  reset() {
    try {
      mixpanel.reset()
      this.register(true)
    } catch (err) {
      captureFatalException(`Failed to reset user after logout : ${err}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  track(properties: Record<string, unknown>) {
    try {
      if (!isBotAgent()) {
        if (this.isInitialised) {
          this.fetchDependencies(properties)
          this.emptyMpQueue()
          this.sendEvent(properties)
        } else {
          this.pushToMpQueue(properties)
        }
      }
    } catch (err) {
      captureFatalException(`Failed to track event with mixpanel : ${err}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  trackWithSgtm(properties: Record<string, unknown>) {
    try {
      if (!isBotAgent()) {
        const transformedProperties = getTransformedProperties(properties)
        const commonProperties = getCommonProperties(properties)
        if (transformedProperties) {
          const serverProperties = {
            ...transformedProperties,
            ...commonProperties.sgtmCommonProperties,
          }
          console.info('Tracking Mixpanel event with sgtm : ', {
            eventName: transformedProperties.event_name,
            serverProperties,
          })
          handleSgtmApi(serverProperties, 'mixpanel_sgtm_api', 'mixpanel_properties')
        }
      }
    } catch (err) {
      captureFatalException(`Failed to track event with mixpanel through sgtm: ${err}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  private getShouldSendToProxy = (event_name: string) => {
    return event_name === 'Page Viewed'
  }

  // Callback that runs after mixpanel is loaded and initialised
  private initialisationCheck = () => {
    console.info('Mixpanel event stream : Initialisation is done')
    this.fetchDependencies()
    this.isInitialised = true
    this.emptyMpQueue()
    console.info('Mixpanel event stream : Initialisation check is done')
  }

  // Generates mixpanel user device id and links it to mixpanel
  private register(shouldResetUserId = false) {
    const uid = this.getUniqueId()
    console.info('Mixpanel event stream : setting mixpanel user device id to ', uid)
    this.setMixpanelUserDeviceId(uid)
    if (shouldResetUserId) {
      this.deleteMixpanelUserId()
    }
  }

  private setMixpanelTentativeEmail = (email: string) => {
    setCookie(ANALYTIC_COOKIES.MIXPANEL_TENTATIVE_EMAIL, email)
  }

  private getMixpanelUserId = (): string | undefined | null => {
    return (
      getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_ID) ?? getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.MIXPANEL_USER_ID)
    )
  }

  private setMixpanelUserId = (userId: string) => {
    setCookie(ANALYTIC_COOKIES.MIXPANEL_USER_ID, userId)
    setInLocalStorageWithExpiry(ANALYTIC_COOKIES.MIXPANEL_USER_ID, userId)
  }

  private deleteMixpanelUserId = () => {
    eraseCookie(ANALYTIC_COOKIES.MIXPANEL_USER_ID)
    eraseFromLocalStorage(ANALYTIC_COOKIES.MIXPANEL_USER_ID)
  }

  private getMixpanelUserDeviceId = (): string | undefined => {
    return getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_DEVICE_ID)
  }

  private setMixpanelUserDeviceId = (userDeviceId: string) => {
    setCookie(ANALYTIC_COOKIES.MIXPANEL_USER_DEVICE_ID, userDeviceId)
  }

  private getUniqueId() {
    let id
    if (crypto.randomUUID) {
      id = crypto.randomUUID()
    } else {
      id = generateAlphaNumericId(36)
    }
    return id
  }

  private fetchDependencies(properties?: Record<string, unknown>) {
    console.info('Mixpanel event stream : Fetching dependencies')
    if (
      !properties?.lb_user_id &&
      !getlbUserId() &&
      !getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.LB_USER_ID, false)
    ) {
      console.info('Mixpanel event stream : lb user id is not present, creating one')
      createLastBrandUserId()
    }

    if (
      !properties?.session_id &&
      !getCookie(ANALYTIC_COOKIES.SESSION_ID) &&
      !getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.SESSION_ID)
    ) {
      console.info('Mixpanel event stream : session id is not present, creating now')
      createNewSessionIfNeeded()
    }

    if (!this.getMixpanelUserDeviceId()) {
      console.info('Mixpanel event stream : mixpanel user device id is not present, creating now')
      this.register()
    }

    const email = AuthUtil.getUser().email
    if (!this.getMixpanelUserId() && AuthUtil.isLoggedIn() && email) {
      console.info('Mixpanel event stream : mixpanel user id is not linked, linking now')
      this.identify({
        email: email,
        isTentativeEmail: false,
      })
    }
  }

  private pushToMpQueue(event: Record<string, unknown>) {
    console.info('Mixpanel event stream : initialisation is pending, pushing to queue ', event)
    this.mpQueue.push(event)
  }

  private emptyMpQueue() {
    const currentQueue = [...this.mpQueue]
    console.info('Mixpanel event stream : emptying queue : ', currentQueue)
    this.mpQueue = []
    currentQueue.forEach(event => {
      this.sendEvent(event)
    })
  }

  private getShouldSendImmediately = (properties: unknown) => {
    if (properties.entity_id === 'start_checkout') {
      return true
    }
    return false
  }

  private sendEvent(properties: Record<string, unknown>) {
    console.info('Mixpanel event stream : Sending event : ', properties)
    const transformedProperties: Record<string, unknown> | null = getTransformedProperties(properties)
    const commonProperties = getCommonProperties(properties)
    if (transformedProperties) {
      const clientProperties = {
        ...transformedProperties,
        ...commonProperties.clientCommonProperties,
      }

      const shouldSendImmediately = this.getShouldSendImmediately(properties)
      const options: RequestOptions = {
        transport: shouldSendImmediately ? 'sendBeacon' : 'xhr',
        send_immediately: shouldSendImmediately,
      }

      type OverridedMixpanelIntf = MixpanelIntf & {
        proxy_instance: MixpanelIntf
      }

      const mixpanelCasted = mixpanel as unknown as OverridedMixpanelIntf
      mixpanelCasted.track(transformedProperties.event_name as string, clientProperties, options)

      const shouldSendToProxy = this.getShouldSendToProxy(transformedProperties.event_name as string)
      if (shouldSendToProxy) {
        mixpanelCasted.proxy_instance.track(transformedProperties.event_name as string, clientProperties, options)
      }
    }
  }
}

const mixpanelCl = new Mixpanel()

export default mixpanelCl
